﻿import { Box, Grid, Link, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getPatientDetail } from '../../redux/actions/healthcareProfessionalActions';
import { selectIsAdmin } from '../../redux/selectors/accountSelectors';
import {
  selectHasPatientName,
  selectPatientDetailError,
  selectedPatientDetail,
} from '../../redux/selectors/patientSelectors';
import NoData from '../NoData';
import PatientValue from './PatientValue';

import { useAppThunkDispatch } from '../../redux/configureStore';

const ParameterTitleTypographyStyle = styled(Typography)(({ theme }) => ({
  display: 'inline',
  component: 'p',
  variant: 'h3',
  color: theme.palette.primary.main,
}));

interface Props {
  selectedPatientId?: string;
  nameOnly?: boolean; // Optional prop
}

export const PatientDetailsHeader: React.FC<Props> = ({
  selectedPatientId,
  nameOnly = false, // Default value is false
}) => {
  const dispatch = useAppThunkDispatch();
  const { t } = useTranslation('patientDetail');
  const navigate = useNavigate();
  const isAdmin = useSelector(selectIsAdmin);
  const hasPatientName = useSelector(selectHasPatientName);
  const patientDetailError = useSelector(selectPatientDetailError);
  const selectedPatient = useSelector(selectedPatientDetail);

  useEffect(() => {
    if (
      selectedPatientId &&
      !patientDetailError &&
      selectedPatient?.accountGuid !== selectedPatientId
    ) {
      dispatch(getPatientDetail(selectedPatientId));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatientId, patientDetailError, selectedPatient?.accountGuid]);

  // While the patient detail request is in flight (or stale data from a previous
  // patient is still in Redux), render nothing to avoid a brief flash of wrong data.
  const isLoading =
    (!selectedPatient && !patientDetailError) ||
    (selectedPatient &&
      selectedPatientId &&
      selectedPatient.accountGuid !== selectedPatientId);

  if (isLoading) {
    return null;
  }

  // Show "nothing to see here" when the API returned an error (e.g. invalid patient ID)
  // or when a non-admin user doesn't have access (patient data loaded but no name)
  if (patientDetailError || (!isAdmin && selectedPatient && !hasPatientName)) {
    return (
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <NoData message="" />
        <Typography>
          {t('header.noAccess')}{' '}
          <Link
            component="button"
            onClick={() => navigate('/dashboard/patients')}
            sx={{ cursor: 'pointer' }}
          >
            {t('header.noAccessLink')}
          </Link>
        </Typography>
      </Box>
    );
  }

  const renderName = () => {
    if (hasPatientName) {
      return <PatientValue patient={selectedPatient ?? undefined} patientKey="name" />;
    }
    // Admin without access - show account GUID
    return (
      <Typography variant="h4" component="h4">
        {t('header.accountGuid', { id: selectedPatientId })}
      </Typography>
    );
  };

  return (
    <Box sx={{ textAlign: 'left' }}>
      {nameOnly ? (
        renderName()
      ) : (
        <>
          {renderName()}
          <Grid container spacing={1}>
            <Grid>
              <ParameterTitleTypographyStyle>
                {t('header.gender')}{' '}
              </ParameterTitleTypographyStyle>
              <PatientValue
                patient={selectedPatient ?? undefined}
                patientKey="gender"
                display="inline"
                variant="body1"
                component="p"
              />
            </Grid>
            <Grid>
              <ParameterTitleTypographyStyle>
                {t('header.age')}{' '}
              </ParameterTitleTypographyStyle>
              <PatientValue
                patient={selectedPatient ?? undefined}
                patientKey="dateOfBirth"
                display="inline"
                variant="body1"
                component="p"
              />
            </Grid>
            {selectedPatient?.weight ? (
              <Grid>
                <ParameterTitleTypographyStyle>
                  {t('header.weight')}{' '}
                </ParameterTitleTypographyStyle>
                <PatientValue
                  patient={selectedPatient ?? undefined}
                  patientKey="weight"
                  display="inline"
                  variant="body1"
                  component="p"
                />
              </Grid>
            ) : null}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default PatientDetailsHeader;
